import React from 'react';
import { Sun, Sunset } from 'lucide-react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ComposedChart,
} from 'recharts';

// --- TIPOS ---
export interface HourlyRACData {
    hour: number;
    rac: number;
    demand?: number;
    nop?: number;          // Staffing capacity (NOP × capacity_util_adj) in mov/h
    lossStaffing: number;
    lossComplexity: number;
    lossIntensity: number;
    cmpInfra: number;
}

export interface RACStratigraphyProps {
    hourlyData: HourlyRACData[];
    peakMorning?: { hour: number; rac: number };
    peakEvening?: { hour: number; rac: number };
    isDark?: boolean;
    language?: 'es' | 'en';
}

// Custom tooltip component
const CustomTooltip = ({
    active,
    payload,
    label,
    isDark,
    language
}: { active?: boolean; payload?: any[]; label?: string | number; isDark?: boolean; language?: 'es' | 'en' }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0]?.payload as HourlyRACData;
    const total = data.rac + data.lossStaffing + data.lossComplexity + data.lossIntensity;

    return (
        <div className={`p-3 rounded-lg shadow-xl border ${isDark
            ? 'bg-gray-900/95 border-[var(--border)] text-white'
            : 'bg-white border-[var(--border)] text-gray-900'
            }`}>
            <p className="font-bold text-sm mb-2">
                {String(label).replace(':00', '')}:00
            </p>
            <div className="space-y-1 text-xs">
                {data.demand != null && data.demand > 0 && (
                    <div className="flex justify-between gap-4">
                        <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                            {language === 'es' ? 'Demanda' : 'Demand'}
                        </span>
                        <span className="font-mono font-bold text-orange-500">{data.demand.toFixed(1)}</span>
                    </div>
                )}
                {data.nop != null && (
                    <div className="flex justify-between gap-4">
                        <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-400"></span>
                            {language === 'es' ? 'Cap. Dotación' : 'Staffing Cap.'}
                        </span>
                        <span className="font-mono font-bold text-red-400">{data.nop.toFixed(1)}</span>
                    </div>
                )}
                <div className="flex justify-between gap-4">
                    <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-cyan-400"></span>
                        RAC
                    </span>
                    <span className="font-mono font-bold">{data.rac.toFixed(1)}</span>
                </div>
                {data.lossStaffing > 0 && (
                    <div className="flex justify-between gap-4">
                        <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                            {language === 'es' ? 'Dotación' : 'Staffing'}
                        </span>
                        <span className="font-mono font-bold text-red-400">-{data.lossStaffing.toFixed(1)}</span>
                    </div>
                )}
                {data.lossComplexity > 0 && (
                    <div className="flex justify-between gap-4">
                        <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            {language === 'es' ? 'Complejidad' : 'Complexity'}
                        </span>
                        <span className="font-mono font-bold text-amber-400">-{data.lossComplexity.toFixed(1)}</span>
                    </div>
                )}
                {data.lossIntensity > 0 && (
                    <div className="flex justify-between gap-4">
                        <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                            {language === 'es' ? 'Intensidad' : 'Intensity'}
                        </span>
                        <span className="font-mono font-bold text-purple-400">-{data.lossIntensity.toFixed(1)}</span>
                    </div>
                )}
                <div className="pt-1 mt-1 border-t border-[var(--border)] flex justify-between gap-4 font-bold">
                    <span>CMP Infra</span>
                    <span className="font-mono text-emerald-400">{data.cmpInfra.toFixed(1)}</span>
                </div>
            </div>
        </div>
    );
};

/**
 * RACStratigraphy - Stacked Bar Chart showing RAC capacity breakdown
 * 
 * Visualizes the "Glass Box" calculation of Real Apron Capacity (RAC):
 * - Cyan (base): Actual RAC capacity
 * - Red: Loss due to insufficient staffing (NOP < required)
 * - Amber: Loss due to complexity factors (CF > 1)
 * - Purple: Loss due to operational intensity (OIF > threshold)
 * - Orange line: Demand overlay
 * - Dashed line: CMP_infra (theoretical infrastructure maximum)
 */
const RACStratigraphy: React.FC<RACStratigraphyProps> = ({
    hourlyData,
    peakMorning,
    peakEvening,
    isDark = true,
    language = 'es',
}) => {
    // Transform data for stacked bar chart
    const chartData = hourlyData.map((d) => ({
        ...d,
        label: `${String(d.hour).padStart(2, '0')}:00`,
    }));

    // Get max value for Y-axis domain — scale to ACTUAL DATA, not CMP Infra
    const maxDemand = Math.max(...hourlyData.map((d) => d.demand || 0), 0);
    const maxStacked = Math.max(
        ...hourlyData.map((d) => d.rac + d.lossStaffing + d.lossComplexity + d.lossIntensity),
        0
    );
    const maxCmpInfra = Math.max(...hourlyData.map((d) => d.cmpInfra), 0);
    const dataMax = Math.max(maxStacked, maxDemand);
    const yMax = Math.ceil(dataMax * 1.2);

    // Colors
    const colors = {
        rac: '#22d3ee',        // Cyan
        demand: '#f97316',     // Orange
        lossStaffing: '#ef4444',   // Red
        lossComplexity: '#f59e0b', // Amber (was gray — more visible now)
        lossIntensity: '#a855f7',  // Purple
        cmpInfra: '#10b981',   // Emerald (dashed line)
        grid: isDark ? '#1e293b' : '#e2e8f0',
        axis: isDark ? '#94a3b8' : '#64748b',
    };

    return (
        <div className="w-full h-full relative">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                    barCategoryGap="15%"
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke={colors.grid}
                    />

                    <XAxis
                        dataKey="label"
                        stroke={colors.axis}
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        interval={1}
                    />

                    <YAxis
                        stroke={colors.axis}
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        domain={[0, yMax]}
                        label={{
                            value: 'mov/h',
                            angle: -90,
                            position: 'insideLeft',
                            style: { fill: colors.axis, fontSize: 11 },
                        }}
                    />

                    <Tooltip
                        content={(props: any) => (
                            <CustomTooltip {...props} isDark={isDark} language={language} />
                        )}
                    />

                    {/* Stacked bars - order matters (bottom to top) */}
                    <Bar
                        dataKey="rac"
                        stackId="capacity"
                        fill={colors.rac}
                        radius={[0, 0, 0, 0]}
                        name="RAC"
                    />
                    <Bar
                        dataKey="lossStaffing"
                        stackId="capacity"
                        fill={colors.lossStaffing}
                        name={language === 'es' ? 'Dotación' : 'Staffing'}
                    />
                    <Bar
                        dataKey="lossComplexity"
                        stackId="capacity"
                        fill={colors.lossComplexity}
                        name={language === 'es' ? 'Complejidad' : 'Complexity'}
                    />
                    <Bar
                        dataKey="lossIntensity"
                        stackId="capacity"
                        fill={colors.lossIntensity}
                        radius={[3, 3, 0, 0]}
                        name={language === 'es' ? 'Intensidad' : 'Intensity'}
                    />

                    {/* Demand line overlay */}
                    <Line
                        type="monotone"
                        dataKey="demand"
                        stroke={colors.demand}
                        strokeWidth={2.5}
                        dot={{ fill: colors.demand, strokeWidth: 0, r: 3 }}
                        activeDot={{ r: 5, fill: colors.demand }}
                        name={language === 'es' ? 'Demanda' : 'Demand'}
                    />
                    {/* Staffing capacity line (NOP × capacity_util_adj) */}
                    <Line
                        type="stepAfter"
                        dataKey="nop"
                        stroke="#f87171"
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        dot={false}
                        activeDot={{ r: 4, fill: '#f87171' }}
                        name={language === 'es' ? 'Cap. Dotación' : 'Staffing Cap.'}
                    />

                    {/* CMP Infrastructure reference line (dashed) */}
                    {maxCmpInfra > 0 && maxCmpInfra <= yMax && (
                        <ReferenceLine
                            y={hourlyData[0]?.cmpInfra || maxCmpInfra}
                            stroke={colors.cmpInfra}
                            strokeDasharray="8 4"
                            strokeWidth={2}
                            label={{
                                value: `CMP Infra: ${hourlyData[0]?.cmpInfra?.toFixed(1) || maxCmpInfra} mov/h`,
                                position: 'insideTopRight',
                                fill: colors.cmpInfra,
                                fontSize: 11,
                                fontWeight: 'bold',
                                offset: 8,
                            }}
                        />
                    )}
                    {/* If CMP is above chart range, show at top edge */}
                    {maxCmpInfra > yMax && (
                        <ReferenceLine
                            y={yMax * 0.97}
                            stroke={colors.cmpInfra}
                            strokeDasharray="8 4"
                            strokeWidth={2}
                            label={{
                                value: `↑ CMP Infra: ${hourlyData[0]?.cmpInfra?.toFixed(1) || maxCmpInfra} mov/h`,
                                position: 'insideTopRight',
                                fill: colors.cmpInfra,
                                fontSize: 11,
                                fontWeight: 'bold',
                                offset: 8,
                            }}
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>

        </div>
    );
};

export default RACStratigraphy;
